import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import DatePickerPopover from 'shared/components/ui/DatePickerPopover';

export function toDateInputValue(v) {
  if (v == null || v === '') return '';
  const s = String(v).trim();
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatDisplayDate(ymd) {
  if (!ymd) return '';
  try {
    const d = parseISO(ymd.length === 10 ? `${ymd}T12:00:00` : ymd);
    return isValid(d) ? format(d, 'dd-MM-yyyy') : ymd;
  } catch {
    return ymd;
  }
}

/**
 * Branded date input with a themed calendar popover (dark mode + orange accent).
 */
export default function DateInput({ value, onChange, className = '', inputClassName = '', onClick, ...props }) {
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const ymd = toDateInputValue(value);
  const displayValue = formatDisplayDate(ymd);

  const updateAnchor = useCallback(() => {
    if (!wrapperRef.current) return;
    setAnchorRect(wrapperRef.current.getBoundingClientRect());
  }, []);

  const openPicker = (e) => {
    onClick?.(e);
    if (props.disabled) return;
    updateAnchor();
    setOpen(true);
  };

  const emitChange = (nextYmd) => {
    onChange?.({ target: { value: nextYmd || '' } });
  };

  const handleSelect = (nextYmd) => {
    emitChange(nextYmd);
    setOpen(false);
  };

  const handleClear = () => {
    emitChange('');
  };

  useEffect(() => {
    if (!open) return undefined;
    const onReposition = () => updateAnchor();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateAnchor]);

  return (
    <>
      <div
        ref={wrapperRef}
        className={`relative inline-flex items-center ${className}`}
      >
        <button
          type="button"
          disabled={props.disabled}
          onClick={openPicker}
          className={`trodo-date-input relative w-full pr-7 text-left ${inputClassName} ${
            props.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          }`}
        >
          <span className={displayValue ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}>
            {displayValue || 'dd-mm-yyyy'}
          </span>
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={openPicker}
          disabled={props.disabled}
          className="absolute right-1 flex h-5 w-5 items-center justify-center rounded text-[color:var(--text-muted)] hover:text-[color:var(--trodo-primary)] disabled:opacity-50"
          aria-label="Open calendar"
        >
          <Calendar size={14} strokeWidth={2} />
        </button>
      </div>
      <DatePickerPopover
        open={open}
        anchorRect={anchorRect}
        value={ymd}
        onSelect={handleSelect}
        onClear={handleClear}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
